import { formatDateOnly, errorWords } from "@alm/domain";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { EmptyState } from "@/components/app/lists/empty-state";
import { PageHeader } from "@/components/app/shell/page-header";
import { PortalNotice } from "@/components/app/patterns/portal-notice";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useSession } from "@/lib/host/auth-client";
import { orpc } from "@/lib/orpc";
import { invalidateKeys } from "@/lib/query/invalidate-keys";
import { relativeTime } from "@/lib/facility/relative-time";
import { Stethoscope } from "@/lib/public-icons";
import { ChangePasswordDialog } from "./-components/change-password-dialog";
import { PhysicianProfileDialog } from "./-components/physician-profile-dialog";
import { PhysicianShell } from "./-components/physician-shell";
import { KIND_LABELS } from "./-components/request-kinds";
import { SignedRequests } from "./-components/signed-requests";

export const Route = createFileRoute("/physician/")({
  component: PhysicianPortal,
});

/** Everything waiting for this doctor's signature. */
function PhysicianPortal() {
  const session = useSession();
  const queryClient = useQueryClient();
  const [chosen, setChosen] = useState<Record<string, boolean>>({});
  const [password, setPassword] = useState("");

  const enabled = Boolean(session.data);
  const profile = useQuery({
    ...orpc.physicianPortal.profile.queryOptions({}),
    retry: false,
    enabled,
  });
  const queue = useQuery({
    ...orpc.physicianPortal.requests.queryOptions({
      input: { status: "pending" },
    }),
    retry: false,
    enabled,
  });

  const sign = useMutation(
    orpc.physicianPortal.sign.mutationOptions({
      onSuccess: async (result) => {
        const skipped = result.skipped.length;
        toast.success(
          skipped > 0
            ? `Signed ${result.signed.length}. ${skipped} could not be signed and were left.`
            : `Signed ${result.signed.length}.`,
        );
        setChosen({});
        setPassword("");
        await invalidateKeys(queryClient, [orpc.physicianPortal.key()]);
      },
      onError: (error: Error) => toast.error(errorWords(error)),
    }),
  );

  if (!session.data) {
    return (
      <PhysicianShell>
        <PortalNotice
          icon={<Stethoscope />}
          title="Sign in to see your documents"
          action={
            <Button asChild size="lg">
              <Link to="/login" search={{ next: "/physician" }}>
                Sign in
              </Link>
            </Button>
          }
          secondary={
            <Button asChild variant="link">
              <Link to="/forgot-password">Email me a reset link</Link>
            </Button>
          }
        >
          Orders, plans of care and notes the community has sent you to sign.
        </PortalNotice>
      </PhysicianShell>
    );
  }

  if (queue.isError) {
    return (
      <PhysicianShell name={session.data.user.name}>
        <PortalNotice icon={<Stethoscope />} tone="idle" title="This is not a physician account">
          If you were expecting documents to sign, ask the community to check how your account is
          set up.
        </PortalNotice>
      </PhysicianShell>
    );
  }

  const rows = queue.data ?? [];
  const selected = rows.filter((row) => chosen[row.id]);
  const provider = profile.data?.provider;
  const doctor = provider
    ? [provider.firstName, provider.lastName].filter(Boolean).join(" ")
    : session.data.user.name;

  return (
    <PhysicianShell name={doctor}>
      <div className="mb-6">
        <PageHeader
          title="Your documents"
          actions={
            <>
              <PhysicianProfileDialog account={profile.data?.account ?? null} />
              <ChangePasswordDialog />
            </>
          }
        />
      </div>

      <Tabs defaultValue="pending">
        <TabsList className="mb-4">
          <TabsTrigger value="pending">
            {rows.length > 0 ? `Waiting (${rows.length})` : "Waiting"}
          </TabsTrigger>
          <TabsTrigger value="signed">Signed</TabsTrigger>
        </TabsList>
        <TabsContent value="signed">
          <SignedRequests />
        </TabsContent>
        <TabsContent value="pending">
          {queue.isPending ? (
            <Skeleton className="h-40 w-full" />
          ) : rows.length === 0 ? (
            <EmptyState
              title="Nothing is waiting"
              description="When a community sends you something to sign it will appear here."
            />
          ) : (
            <div className="space-y-4">
              {/* One list, not a card per document: a physician signing a
                  week's orders reads down a column and ticks. */}
              <Card className="gap-0 py-0">
                <CardContent className="p-0">
                  <div className="flex min-h-11 items-center gap-3 border-b px-4 py-2">
                    <Checkbox
                      id="select-every-request"
                      checked={
                        selected.length === 0
                          ? false
                          : selected.length === rows.length
                            ? true
                            : "indeterminate"
                      }
                      onCheckedChange={(value) =>
                        setChosen(
                          value === true
                            ? Object.fromEntries(rows.map((row) => [row.id, true]))
                            : {},
                        )
                      }
                    />
                    <Label htmlFor="select-every-request" className="font-normal">
                      Select all {rows.length}
                    </Label>
                  </div>
                  <ul className="divide-border-soft divide-y">
                    {rows.map((row) => (
                      <li key={row.id} className="flex items-start gap-3 px-4 py-3">
                        <Checkbox
                          aria-label={`Select ${KIND_LABELS[row.entityType] ?? row.entityType}`}
                          checked={Boolean(chosen[row.id])}
                          className="mt-1"
                          onCheckedChange={(value) =>
                            setChosen((previous) => ({
                              ...previous,
                              [row.id]: value === true,
                            }))
                          }
                        />
                        <div className="min-w-0 flex-1">
                          <Link
                            className="text-base font-medium hover:underline"
                            params={{ requestId: row.id }}
                            to="/physician/$requestId"
                          >
                            {KIND_LABELS[row.entityType] ?? row.entityType}
                          </Link>
                          <p className="text-muted-foreground mt-0.5 text-sm">
                            {[row.residentFirstName, row.residentLastName]
                              .filter(Boolean)
                              .join(" ")}
                            {row.facilityName ? `, ${row.facilityName}` : ""}
                          </p>
                        </div>
                        {/* Relative, as every list in the app reads; the date on hover. */}
                        <time
                          className="text-muted-foreground shrink-0 pt-0.5 text-sm"
                          dateTime={new Date(row.requestedAt).toISOString()}
                          title={formatDateOnly(row.requestedAt, row.facilityTimezone ?? undefined)}
                        >
                          Sent {relativeTime(row.requestedAt, row.facilityTimezone ?? undefined)}
                        </time>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="space-y-3 p-4">
                  <div className="space-y-2">
                    <Label htmlFor="signing-password">Your password</Label>
                    <Input
                      autoComplete="current-password"
                      id="signing-password"
                      type="password"
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                    />
                    <p className="text-muted-foreground text-sm">
                      Asked for again because a signature has to be yours, not whoever is at this
                      screen.
                    </p>
                  </div>
                  {selected.length === 0 ? (
                    <p className="text-muted-foreground text-sm">
                      Tick the documents to sign above, then enter your password.
                    </p>
                  ) : null}
                  <Button
                    size="lg"
                    disabled={sign.isPending || selected.length === 0 || !password}
                    onClick={() =>
                      sign.mutate({
                        requestIds: selected.map((row) => row.id),
                        password,
                      })
                    }
                  >
                    {sign.isPending
                      ? "Signing…"
                      : `Sign ${selected.length || ""} ${selected.length === 1 ? "document" : "documents"}`.trim()}
                  </Button>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </PhysicianShell>
  );
}
